import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiRequestError } from '../api/client';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { useAuth } from '../auth/AuthContext';

type ServerStatus = 'checking' | 'connected' | 'disconnected';

/** Pantalla de ingreso con el diseño del "gate" del prototipo: nombre de usuario (o correo) y contraseña. */
export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [status, setStatus] = useState<ServerStatus>('checking');
  const [dbVersion, setDbVersion] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .health()
      .then((h) => {
        if (!alive) return;
        setStatus('connected');
        setDbVersion(h.database.version);
      })
      .catch(() => alive && setStatus('disconnected'));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!loading && user) navigate('/app', { replace: true });
  }, [loading, user, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!identifier.trim() || !password) {
      setError(t('login.required'));
      return;
    }
    setBusy(true);
    try {
      await login(identifier, password);
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) setError(t(`errors.${err.code}`, { ...err.params, defaultValue: t('errors.UNKNOWN', { code: err.code }) }));
      else setError(t('errors.UNKNOWN', { code: 'CLIENT' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate" id="gate">
      <div className="gatecard">
        <div className="gatelogo">
          <img src="/teo-logo.png" alt="TEO Inventory Control" />
          <h1>TEO</h1>
          <p>{t('login.subtitle')}</p>
        </div>
        <form className="loginform" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="field">
            <label htmlFor="user">{t('login.user')}</label>
            <div className="inputwrap">
              <svg className="lead" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20.5c0-3.6 3.6-6 8-6s8 2.4 8 6" />
              </svg>
              <input
                id="user" name="user" type="text" className="input" autoComplete="username" autoFocus autoCapitalize="none" spellCheck={false}
                placeholder={t('login.userPlaceholder')} value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="password">{t('login.password')}</label>
            <div className="inputwrap">
              <svg className="lead" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
                <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
              </svg>
              <input
                id="password" name="password" type={showPassword ? 'text' : 'password'} className="input" autoComplete="current-password"
                placeholder={t('login.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))} onBlur={() => setCapsLock(false)}
              />
              <button
                type="button" className="reveal" id="togglePassword" aria-pressed={showPassword}
                aria-label={t(showPassword ? 'login.hidePassword' : 'login.showPassword')} title={t(showPassword ? 'login.hidePassword' : 'login.showPassword')}
                onClick={() => setShowPassword((v) => !v)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z" />
                  <circle cx="12" cy="12" r="2.8" />
                  {showPassword ? <path d="m4 4 16 16" /> : null}
                </svg>
              </button>
            </div>
            {capsLock ? (
              <div className="capslock" id="capsLockWarning" role="status">
                {t('login.capsLock')}
              </div>
            ) : null}
          </div>
          <button type="submit" className="btn loginbtn" id="loginSubmit" disabled={busy}>
            {busy ? t('login.submitting') : t('login.submit')}
          </button>
        </form>
        {error ? (
          <div className="help loginnotice loginerror" role="alert">
            {error}
          </div>
        ) : null}
        <div className="help" style={{ textAlign: 'center', marginTop: 12 }}>
          {t('login.help')}
        </div>
        <div className="loginfoot">
          <span className={`serverstatus ${status}`} data-status={status} data-db-version={dbVersion ?? ''}>
            <span className="dot" />
            {t(`login.server.${status}`)}
          </span>
          <LanguageSwitch />
        </div>
      </div>
    </div>
  );
}
